"use client"

import { useEffect, useState } from "react"
import Link from "next/link"
import { Rabbit, Facebook, Instagram } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

// Define category type
type Category = {
  id: number
  name: string
  slug: string
  parentId: number | null
}

export function Footer() {
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchCategories() {
      try {
        setLoading(true)
  
        const cached = localStorage.getItem("mainCategories")
        if (cached) {
          setCategories(JSON.parse(cached))
          setLoading(false)
          return
        }
  
        const response = await fetch("/api/categories")
        if (!response.ok) throw new Error("Failed to fetch categories")
  
        const data = await response.json()
        const mainCategories = data.categories.filter((cat: Category) => cat.parentId === null)
  
        setCategories(mainCategories)
        localStorage.setItem("mainCategories", JSON.stringify(mainCategories))
      } catch (err) {
        console.error("Error fetching categories:", err)
        setError("Неуспешно зареждане на категориите.")
      } finally {
        setLoading(false)
      }
    }
  
    fetchCategories()
  }, [])

  return (
    <footer>
      {/* Main Footer */}
      <div className="bg-[#1C1C1C] text-white">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
            {/* Logo and Social */}
            <div className="lg:col-span-2">
              <div className="flex items-center gap-2 mb-6">
                <Rabbit className="w-8 h-8 text-blue-500" />
                <span className="text-2xl font-bold">LV-Vet</span>
              </div>
              <div className="flex gap-4">
                <Link href="#" className="text-white hover:text-gray-300">
                  <Instagram className="w-6 h-6" />
                </Link>
                <Link href="#" className="text-white hover:text-gray-300">
                  <Facebook className="w-6 h-6" />
                </Link>
              </div>
            </div>

            {/* За клиенти */}
            <div className="lg:col-span-2">
              <h3 className="font-bold mb-4">За клиенти</h3>
              <ul className="space-y-2">
                <li>
                  <Link href="/customer/profile" className="hover:text-gray-300">
                    Профил
                  </Link>
                </li>
                <li>
                  <Link href="/claims" className="hover:text-gray-300">
                    Рекламации
                  </Link>
                </li>
              </ul>
            </div>

            {/* Продукти */}
            <div className="lg:col-span-2">
              <h3 className="font-bold mb-4">Продукти</h3>
              {loading ? (
                <p className="text-gray-400">Зареждане...</p>
              ) : error ? (
                <p className="text-red-400">{error}</p>
              ) : (
                <ul className="space-y-2">
                  {categories.map((category) => (
                    <li key={category.id}>
                      <Link href={`/${category.slug}`} className="hover:text-gray-300">
                        {category.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Полезно */}
            <div className="lg:col-span-2">
              <h3 className="font-bold mb-4">Полезно</h3>
              <ul className="space-y-2">
                <li>
                  <Link href="/about" className="hover:text-gray-300">
                    За нас
                  </Link>
                </li>
                <li>
                  <Link href="/delivery" className="hover:text-gray-300">
                    Доставка и плащане
                  </Link>
                </li>
                <li>
                  <Link href="/claims" className="hover:text-gray-300">
                    Рекламации
                  </Link>
                </li>
                <li>
                  <Link href="/faq" className="hover:text-gray-300">
                    Въпроси и отговори
                  </Link>
                </li>
                <li>
                  <Link href="/terms" className="hover:text-gray-300">
                    Условия за ползване
                  </Link>
                </li>
                <li>
                  <Link href="/blog" className="hover:text-gray-300">
                    Блог
                  </Link>
                </li>
                <li>
                  <Link href="/contact" className="hover:text-gray-300">
                    Контакти
                  </Link>
                </li>
                <li>
                  <Link href="/stores" className="hover:text-gray-300">
                    Магазини
                  </Link>
                </li>
              </ul>
            </div>

            {/* Newsletter */}
            <div className="lg:col-span-4">
              <h3 className="font-bold mb-4">Ел. Бюлетин:</h3>
              <p className="text-sm mb-4">
                Абонирай се за нашия бюлетин и ще получиш промо код за <span className="text-[#FFD84D]">10%</span>{" "}
                отстъпка при първа поръчка. Промо кода важи за всички продукти, които не са в промоция.
              </p>
              <div className="flex gap-2 mb-6">
                <Input type="email" placeholder="Твоят e-mail..." className="bg-white text-black" />
                <Button className="bg-blue-500 hover:bg-blue-800 text-white whitespace-nowrap">ЗАПИШИ СЕ</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}
